"use client"

import { useState } from "react"
import { usePathname } from "next/navigation"
import { useGoogleReCaptcha } from "@/lib/recaptcha-client"
import { motion } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { FileText, Send, CheckCircle2, Clock, DollarSign, MessageSquare, Loader2 } from "lucide-react"
import { CTA } from "@/components/sections/CTA"
import { MultiSelectCombobox } from "@/components/ui/multi-select-combobox"
import { type Locale } from "@/lib/i18n"

export default function RequestFormPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: [] as string[],
    deadline: "",
    message: "",
  })

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError("")
    setSubmitting(true)

    try {
      // Get reCAPTCHA token
      let recaptchaToken = ""
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("quote_form")
      }

      const response = await fetch('/api/quote/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setSubmitError(data.error || 'Bir hata oluştu. Lütfen tekrar deneyin.')
        setSubmitting(false)
        return
      }

      setSubmitSuccess(true)
      // Reset form
      setFormData({
        name: "",
        company: "",
        email: "",
        phone: "",
        service: [],
        deadline: "",
        message: "",
      })

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false)
      }, 5000)
    } catch (error) {
      setSubmitError('Bir hata oluştu. Lütfen tekrar deneyin.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    // Çoklu hizmet seçimi
    if (name === "service" && e.target instanceof HTMLSelectElement && e.target.multiple) {
      const selectedValues = Array.from(e.target.selectedOptions).map((option) => option.value)
      setFormData((prev) => ({
        ...prev,
        service: selectedValues,
      }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const benefits = [
    { icon: CheckCircle2, text: "24 Saat İçinde Yanıt" },
    { icon: DollarSign, text: "Ücretsiz Teklif" },
    { icon: Clock, text: "Hızlı Çözüm" },
    { icon: MessageSquare, text: "Uzman Destek" },
  ]

  const serviceOptions = [
    { value: "web-tasarim", label: "Web Tasarım" },
    { value: "web-yazilim", label: "Web Yazılım" },
    { value: "ui-ux-tasarim", label: "UI & UX Tasarım" },
    { value: "mobil-uygulama", label: "Mobil Uygulama" },
    { value: "masaustu-uygulama", label: "Masaüstü Uygulama" },
    { value: "e-ticaret", label: "E-ticaret Sistemleri" },
    { value: "shopify", label: "Shopify Tasarım - Geliştirme" },
    { value: "woocommerce", label: "WooCommerce Tasarım - Geliştirme" },
    { value: "otomasyon", label: "Otomasyon Çözümleri" },
    { value: "ai-servisler", label: "AI Ürünleştirilmiş Servisler" },
    { value: "diger", label: "Diğer" },
  ]

  return (
    <>
      {/* Hero Section */}
      <Section className="relative overflow-hidden bg-gradient-to-br from-[rgb(3,74,166)] via-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white pt-24 pb-16 sm:pt-32 sm:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex p-4 bg-white/10 backdrop-blur-sm rounded-2xl mb-8"
          >
            <FileText className="h-12 w-12 sm:h-16 sm:w-16" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 font-mono"
          >
            Teklif Al
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-8"
          >
            Projeniz için detaylı teklif almak için formu doldurun. Uzman ekibimiz en kısa sürede size özel çözümler sunacak.
          </motion.p>
          
          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <div key={index} className="flex flex-col items-center gap-2 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
                  <Icon className="h-6 w-6 sm:h-8 sm:w-8" />
                  <span className="text-xs sm:text-sm text-center font-medium">{benefit.text}</span>
                </div>
              )
            })}
          </motion.div>
        </div>
      </Section>

      {/* Form Section */}
      <Section className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
          >
            <form onSubmit={handleSubmit} className="p-6 sm:p-8 lg:p-12">
              {/* Kişisel Bilgiler */}
              <div className="space-y-6 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <FileText className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 font-mono">Kişisel Bilgiler</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Ad Soyad *</label>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Adınız ve Soyadınız"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Şirket Adı</label>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Şirket Adınız"
                      value={formData.company}
                      onChange={handleChange}
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">E-posta Adresi *</label>
                    <Input
                      type="email"
                      name="email"
                      placeholder="ornek@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Telefon Numarası *</label>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="05XX XXX XX XX"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                </div>
              </div>

              {/* Proje Detayları */}
              <div className="space-y-6 pt-8 border-t border-slate-200 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 font-mono">Proje Detayları</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Hizmet(ler)i Seçiniz *</label>
                    <MultiSelectCombobox
                      options={serviceOptions}
                      value={formData.service}
                      onChange={(selected) => {
                        setFormData({ ...formData, service: selected })
                      }}
                      placeholder="Hizmet seçiniz..."
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Hedef Teslimat Süresi</label>
                    <select
                      name="deadline"
                      value={formData.deadline}
                      onChange={handleChange}
                      className="flex h-12 w-full rounded-md border border-slate-300 bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(3,74,166)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Teslimat süresi seçiniz</option>
                      <option value="0-2-hafta">0-2 Hafta</option>
                      <option value="2-4-hafta">2-4 Hafta</option>
                      <option value="4-8-hafta">4-8 Hafta</option>
                      <option value="8-10-hafta">8-10 Hafta</option>
                      <option value="10-hafta-ustu">10 Hafta ve yukarısı</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Mesaj */}
              <div className="space-y-6 pt-8 border-t border-slate-200 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 font-mono">Proje Hakkında</h2>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Proje Detayları *</label>
                  <Textarea
                    name="message"
                    placeholder="Projeniz hakkında detaylı bilgi veriniz. Hedefleriniz, beklentileriniz ve özel gereksinimlerinizi paylaşabilirsiniz..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={8}
                    className="min-h-[200px] border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)] resize-none"
                  />
                </div>
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="pt-4">
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {submitError}
                  </div>
                </div>
              )}

              {/* Success Message */}
              {submitSuccess && (
                <div className="pt-4">
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    Teklif talebiniz alındı! En kısa sürede size dönüş yapacağız.
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-6 border-t border-slate-200">
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full sm:w-auto bg-[rgb(3,74,166)] hover:bg-[rgb(3,62,140)] text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Teklif İste
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}


